<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import { useAgentEditor } from 'dashboard/permissionSets/useAgentEditor';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, required: true },
  email: { type: String, default: '' },
  type: { type: String, default: '' },
  availability: { type: String, default: '' },
  provider: { type: String, default: '' },
  customRoleId: { type: Number, default: null },
  permissionSetId: { type: Number, default: null },
});
const emit = defineEmits(['close']);
const {
  agentName,
  agentAvailability,
  selectedRoleId,
  v$,
  uiFlags,
  pageTitle,
  roles,
  availabilityStatuses,
  editAgent,
  resetPassword,
  canSubmit,
  rolesLoading,
  rolesError,
  load,
} = useAgentEditor(
  {
    id: props.id,
    name: props.name,
    email: props.email,
    type: props.type,
    availability: props.availability,
    customRoleId: props.customRoleId,
    permissionSetId: props.permissionSetId,
  },
  () => emit('close')
);
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="w-full" @submit.prevent="editAgent">
      <div v-if="rolesError" role="alert" class="mb-4 text-n-ruby-11">
        {{ $t('PERMISSION_SETS.ASSIGNMENT.LOAD_ERROR') }}
        <Button
          type="button"
          :label="$t('PERMISSION_SETS.RETRY')"
          @click="load"
        />
      </div>
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
          <select
            v-model="selectedRoleId"
            :disabled="rolesLoading || rolesError"
            @change="v$.selectedRoleId.$touch"
          >
            <option
              v-for="role in roles"
              :key="role.id"
              :value="role.id"
              :disabled="role.disabled"
            >
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentAvailability.$error }">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          <select
            v-model="agentAvailability"
            @change="v$.agentAvailability.$touch"
          >
            <option
              v-for="status in availabilityStatuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
          <span v-if="v$.agentAvailability.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
          </span>
        </label>
      </div>

      <div class="flex flex-row justify-start w-full gap-2 px-0 py-2">
        <div class="w-[50%] ltr:text-left rtl:text-right">
          <Button
            v-if="provider !== 'saml'"
            ghost
            type="button"
            icon="i-lucide-lock-keyhole"
            class="!px-2"
            :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
            @click.prevent="resetPassword"
          />
        </div>
        <div class="w-[50%] flex justify-end items-center gap-2">
          <Button
            faded
            slate
            type="reset"
            :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            :label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="!canSubmit"
            :is-loading="uiFlags.isUpdating"
          />
        </div>
      </div>
    </form>
  </div>
</template>
